import { apiFetch } from './api.ts';

export interface Me {
  phone: string | null;
  subscribed: boolean;
}

export function fetchMe(): Promise<Me> {
  return apiFetch<Me>('/me');
}

export function authContinue(phone: string): Promise<Me> {
  return apiFetch<Me>('/auth/continue', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
}

export interface Account {
  signedIn: boolean;
  phone: string | null;
  subscribed: boolean;
  status: string;
  isCancelled: boolean;
  isPaused: boolean;
  plan: string | null;
  amount: string | null;
  gateway: string | null;
  subStart: string | null;
  subEnd: string | null;
  memberSince: string | null;
}

export function fetchAccount(): Promise<Account> {
  return apiFetch<Account>('/account');
}

export function logout(): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('/auth/logout', { method: 'POST' });
}

export function softSubscribe(phone: string, plan: string): Promise<{ subscribed: boolean }> {
  return apiFetch<{ subscribed: boolean }>('/subscribe', {
    method: 'POST',
    body: JSON.stringify({ phone, plan }),
  });
}

export interface SubscribeResult {
  bypass?: boolean;
  // SabPaisa
  checkoutUrl?: string;
  clientSecret?: string;
  // PayU
  payuForm?: {
    action: string;
    fields: Record<string, string>;
  };
  plan?: string;
}

export function initPayment(
  phone: string,
  plan: string,
  clickid?: string,
  gateway?: string,
): Promise<SubscribeResult> {
  return apiFetch<SubscribeResult>('/subscriptions/create', {
    method: 'POST',
    body: JSON.stringify({ phone, plan, clickid, gateway }),
  });
}

/** Submit a PayU hidden form POST — takes over the page (Bolt fallback). */
export function submitPayuForm(action: string, fields: Record<string, string>): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Called after PayU Bolt JS fires its success callback to activate the subscription. */
export function verifyPayment(txnid: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('/payment/verify', {
    method: 'POST',
    body: JSON.stringify({ txnid }),
  });
}

/**
 * Poll /api/payment/status every 3s while the Bolt popup is open.
 * Handles Android UPI: the surl fires on the server even when Bolt's JS
 * callback doesn't, so the poller catches the success.
 * Returns a cancel function — call it when Bolt callback fires first.
 */
export function startPaymentPoller(
  txnid: string,
  onSuccess: () => void,
): () => void {
  let cancelled = false;
  const tick = async () => {
    if (cancelled) return;
    try {
      const res = await apiFetch<{ status: string }>(`/payment/status?txnid=${encodeURIComponent(txnid)}`);
      if (res.status === 'success') { onSuccess(); return; }
    } catch { /* keep polling */ }
    if (!cancelled) setTimeout(tick, 3000);
  };
  setTimeout(tick, 4000); // first check after 4s — give surl time to run
  return () => { cancelled = true; };
}
